import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { useOptionalTeamContext } from '@/contexts/TeamContext'

/**
 * Hook to get all available subscription plans
 */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const response = await apiService.getSubscriptionPlans()
      return response.data || []
    },
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours - plans rarely change
  })
}

/**
 * Hook to get the user's current subscription
 */
export function useCurrentSubscription() {
  const teamContext = useOptionalTeamContext()
  const teamId = teamContext?.currentTeam?.team?.id

  return useQuery({
    queryKey: ['current-subscription', teamId],
    queryFn: async () => {
      const response = await apiService.getCurrentSubscription()
      return response.data
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - reduces server load
    refetchOnWindowFocus: true, // Refresh when user returns to tab (improves UX)
  })
}

/**
 * Hook to get full billing overview (subscription, invoices, payment method)
 */
export function useBillingOverview(options: { enabled?: boolean } = {}) {
  const teamContext = useOptionalTeamContext()
  const teamId = teamContext?.currentTeam?.team?.id

  return useQuery({
    queryKey: ['billing-overview', teamId],
    queryFn: async () => {
      const response = await apiService.getBillingOverview()
      return response.data
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - reduces server load
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  })
}

/**
 * Hook to create a subscription checkout session
 */
export function useCreateSubscriptionCheckout() {
  return useMutation({
    mutationFn: async ({ planId, returnUrl }: { planId: string; returnUrl?: string }) => {
      const response = await apiService.createSubscriptionCheckout(planId, returnUrl)
      return response.data
    },
    onError: (error) => {
      console.error('Failed to create checkout session:', error)
    },
  })
}

/**
 * Hook to verify checkout session completion
 */
export function useVerifyCheckout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiService.verifySubscriptionCheckout(sessionId)
      return response.data
    },
    onSuccess: () => {
      // Invalidate subscription queries to reflect new subscription
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })
}

/**
 * Hook to cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (immediately?: boolean) => {
      const response = await apiService.cancelSubscription(immediately)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })
}

/**
 * Accepting one of the two trial saves.
 *
 * These live here, as hooks, rather than as bare apiService calls in the
 * page, for one reason: everything in this file invalidates
 * current-subscription and billing-overview on success, and the versions that
 * did not shipped a bug straight to production. The extension moved the trial
 * in Stripe and in our database, and the billing page went on saying the
 * customer would be charged the next day, because nothing told it to look
 * again. A wrong billing date is the single worst thing that page can say.
 */
export function useExtendTrial() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (feedbackId: string) => {
      const response = await apiService.acceptTrialExtension(feedbackId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })
}

export function useDowngradeTrialToEssential() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (feedbackId: string) => {
      const response = await apiService.acceptTrialDowngrade(feedbackId)
      return response.data
    },
    onSuccess: () => {
      /* The plan changed as well as the date, so the tier gates and the
         credit figures both need to look again. */
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      queryClient.invalidateQueries({ queryKey: ['user-credits'] })
      /* The real key is tier-usage: it holds the plan limits every gate reads. */
      queryClient.invalidateQueries({ queryKey: ['tier-usage'] })
    },
  })
}

/**
 * Hook to reactivate subscription (undo cancel at period end)
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.reactivateSubscription()
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
    },
  })
}

/**
 * Hook to pause subscription for one billing period
 */
export function usePauseSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.pauseSubscription()
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
    },
  })
}

/**
 * Hook to resume a paused subscription early
 */
export function useResumePausedSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.resumePausedSubscription()
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
    },
  })
}

/**
 * Hook to upgrade monthly subscription to annual
 */
export function useUpgradeToAnnual() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.upgradeToAnnual()
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['user-credits'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })
}

/**
 * Hook to create SetupIntent for updating payment method
 */
export function useCreatePaymentMethodSetup() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiService.createPaymentMethodSetup()
      return response.data
    },
  })
}

/**
 * Hook to update default payment method
 */
export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const response = await apiService.updatePaymentMethod(paymentMethodId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
    },
  })
}

/**
 * Combined hook for subscription state management
 * Provides all subscription-related data and mutations in one place
 */
export function useSubscription(options: { includeBilling?: boolean } = {}) {
  const plansQuery = useSubscriptionPlans()
  const subscriptionQuery = useCurrentSubscription()
  const billingQuery = useBillingOverview({ enabled: options.includeBilling ?? false })

  const subscription = subscriptionQuery.data || null
  const hasActiveSubscription = subscription !== null &&
    ['active', 'trialing'].includes(subscription.status)
  const isPastDue = subscription?.status === 'past_due'
  const isCanceling = subscription?.cancelAtPeriodEnd === true
  const isPaused = subscription?.pausedAt !== null && subscription?.pausedAt !== undefined

  // Pro Features Launch Gate
  // When VITE_PRO_FEATURES_ENABLED is 'false', isPro returns false for everyone
  // This allows deploying Pro code changes while keeping features hidden
  // Set to 'true' (or remove) when ready to launch Pro features publicly
  const proFeaturesEnabled = import.meta.env.VITE_PRO_FEATURES_ENABLED !== 'false'

  return {
    // Data
    plans: plansQuery.data || [],
    subscription,
    billing: billingQuery.data || null,

    // Derived states
    hasActiveSubscription,
    isPastDue,
    isCanceling,
    isPaused,
    isPro: proFeaturesEnabled && hasActiveSubscription,

    // Loading states
    isLoadingPlans: plansQuery.isLoading,
    isLoadingSubscription: subscriptionQuery.isLoading,
    isLoadingBilling: billingQuery.isLoading,
    isLoading: plansQuery.isLoading || subscriptionQuery.isLoading,

    // Error states
    plansError: plansQuery.error,
    subscriptionError: subscriptionQuery.error,
    billingError: billingQuery.error,

    // Refetch functions
    refetchSubscription: subscriptionQuery.refetch,
    refetchBilling: billingQuery.refetch,
  }
}

/** Starting billing is an explicit, non-retrying action against a confirmed subscription. */
export function useEndTrialNow() {
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: async (confirmation: { teamId: string; subscriptionId: string; stripePriceId: string }) => {
      const response = await apiService.endTrialNow(confirmation)
      if (!response.success || !response.data?.ok) {
        throw new Error(response.data?.message || 'Your paid subscription could not be started. Check your payment method and try again.')
      }
      return response.data
    },
    onSettled: async () => {
      // Also refresh after a failure: Stripe may have changed while this view was open.
      await Promise.all(['current-subscription', 'billing-overview', 'tier-usage', 'all-invoices', 'user-profile', 'team'].map(key =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ))
    },
  })
}
